import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q8hacnlsngrm4lltc0';

const seoTitle = '关于我们——学至成成人在线学习平台';
const seoDesc = '学至成是领先的成人在线学习平台，专注为职场人士提供高质量在线课程和职业技能培训。了解我们的品牌故事、核心优势与联系方式。';
const seoKeywords = '成人在线教育平台,关于学至成,在线学习平台介绍,成人职业技能培训';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于学至成',
  description: seoDesc,
  url: 'https://www.example.com/about',
  inLanguage: 'zh-CN',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '学至成',
  url: 'https://www.example.com/',
  description: '学至成是领先的成人在线学习平台，致力于为职场人士提供高质量的在线课程和职业技能培训。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@xuezhicheng.com',
    availableLanguage: ['Chinese'],
  },
};

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSubmitted(true);
      return;
    }

    try {
      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
  };

  const milestones = [
    { year: '2020', title: '平台成立', desc: '学至成正式上线，首批推出50门精品课程' },
    { year: '2022', title: '学员突破10万', desc: '课程数量突破200门，覆盖8大职业技能领域' },
    { year: '2024', title: '行业领先', desc: '累计学员超过50万，合作讲师200+，好评率98%' },
    { year: '2026', title: '持续创新', desc: '引入AI个性化学习推荐，打造智能化成人在线学习体验' },
  ];

  const advantages = [
    {
      icon: 'ri-book-open-line',
      title: '课程体系完善',
      desc: '500+精品课程覆盖IT编程、设计创意、商业管理、语言学习、职业技能、资格证书六大领域，满足不同学员的学习需求。',
    },
    {
      icon: 'ri-user-star-line',
      title: '名师荟萃',
      desc: '200+行业资深讲师，平均10年以上实战经验，既有理论深度又有实践指导，确保课程质量。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '品质保障',
      desc: '严格的课程审核机制，7天无理由退款保障，98%学员好评率，让你零风险开启学习之旅。',
    },
    {
      icon: 'ri-heart-line',
      title: '用心服务',
      desc: '专属学习社群、讲师在线答疑、学习进度追踪，全方位陪伴式服务，让学习之路不再孤单。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl="https://www.example.com/about"
        schemaData={{ '@context': 'https://schema.org', '@graph': [aboutSchema, orgSchema] }}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-900 mb-4">
                关于学至成
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                学至成是领先的成人在线学习平台，致力于为职场人士提供高质量的在线课程和职业技能培训。我们相信，学习不应该受时间和空间的限制，每个人都应该有机会通过持续学习实现职业目标与个人成长。
              </p>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-12">
              发展历程
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {milestones.map((m) => (
                <div key={m.year} className="relative p-6 rounded-2xl bg-background-50 border border-background-200/60 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <span className="font-heading font-bold text-lg">{m.year}</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{m.title}</h3>
                  <p className="text-foreground-600 text-sm">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-12">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((adv) => (
                <div key={adv.title} className="p-6 rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all duration-300">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${adv.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{adv.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 text-center mb-12">
              联系我们
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">电子邮箱</h4>
                    <p className="text-foreground-600 text-sm">contact@xuezhicheng.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">客服电话</h4>
                    <p className="text-foreground-600 text-sm">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">公司地址</h4>
                    <p className="text-foreground-600 text-sm">北京市海淀区中关村科技园创新大厦18层</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground-900 text-sm">工作时间</h4>
                    <p className="text-foreground-600 text-sm">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3 bg-background-50 rounded-2xl p-6 border border-background-200/60">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
                {formSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h4 className="font-heading text-xl font-semibold text-foreground-900 mb-2">消息已发送！</h4>
                    <p className="text-foreground-600 text-sm">感谢您的留言，我们会在1-2个工作日内与您联系。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="name">姓名</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="email">邮箱</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="subject">主题</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="message">消息内容</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的消息（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    {formError && (
                      <p className="text-red-500 text-sm mb-4">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}